import React from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';

type DryRunAcaoTipo = 'add' | 'update' | 'remove' | 'move' | 'error';

export type DryRunAcao = {
    area: string;
    tipo: DryRunAcaoTipo;
    dados?: Record<string, unknown> | null;
    anterior?: Record<string, unknown> | null;
    status: 'pending' | 'error' | 'success';
    erro?: string | null;
};

type Props = {
    applianceNome?: string;
    show: boolean;
    loading: boolean;
    acoes: DryRunAcao[];
    erro?: string | null;
    onClose: () => void;
    /**
     * Quando informado, o modal ganha um botão para seguir do dry run direto
     * para o sincronismo real. Sem ele o modal segue apenas informativo, como
     * na tela de appliances.
     */
    onConfirm?: () => void;
    confirmLabel?: string;
    /**
     * Sincronismo em andamento depois do confirm: o botão fica desabilitado
     * com spinner até o fim (o chamador fecha o modal quando terminar).
     */
    confirmLoading?: boolean;
};

const TIPO_LABEL: Record<DryRunAcaoTipo, string> = {
    add: 'Adicionar',
    update: 'Atualizar',
    remove: 'Remover',
    move: 'Mover',
    error: 'Erro',
};

const CAMPOS_OCULTOS_DR = new Set(['.id', 'place-before', '_order']);

const DryRunDadosCell = ({
    dados,
    anterior,
    erro,
}: {
    dados?: Record<string, unknown> | null;
    anterior?: Record<string, unknown> | null;
    erro?: string | null;
}) => {
    if (erro) {
        return (
            <span style={{ color: 'var(--tc-color-red-700)', fontFamily: 'monospace', fontSize: '12px' }}>
                {erro}
            </span>
        );
    }
    if (!dados || Object.keys(dados).length === 0) return <span style={{ color: 'var(--tc-color-gray-400)' }}>-</span>;

    const entries = Object.entries(dados).filter(([k]) => !CAMPOS_OCULTOS_DR.has(k));
    if (entries.length === 0) return <span style={{ color: 'var(--tc-color-gray-400)' }}>-</span>;

    return (
        <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.6' }}>
            {entries.map(([k, v]) => {
                const anteriorVal = anterior?.[k];
                const changed = anterior != null && k in anterior && String(anteriorVal ?? '') !== String(v ?? '');
                return (
                    <React.Fragment key={k}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--tc-color-gray-500)', flexShrink: 0 }}>{k}:</span>
                            <span
                                style={{
                                    color: 'var(--tc-color-gray-800)',
                                    wordBreak: 'break-all',
                                    background: changed ? 'var(--tc-color-blue-50)' : undefined,
                                    borderRadius: changed ? '2px' : undefined,
                                    padding: changed ? '0 2px' : undefined,
                                }}
                            >
                                {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
                            </span>
                        </div>
                        {changed && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '4px',
                                    flexWrap: 'wrap',
                                    color: 'var(--tc-color-gray-400)',
                                    marginBottom: '2px',
                                    paddingLeft: '8px',
                                }}
                            >
                                <span style={{ flexShrink: 0 }}>↑ era:</span>
                                <span
                                    style={{
                                        wordBreak: 'break-all',
                                        textDecoration: 'line-through',
                                        opacity: 0.75,
                                    }}
                                >
                                    {typeof anteriorVal === 'object'
                                        ? JSON.stringify(anteriorVal)
                                        : String(anteriorVal ?? '')}
                                </span>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};


const TIPO_COLOR: Record<DryRunAcaoTipo, string> = {
    add: 'var(--tc-color-green-700)',
    update: 'var(--tc-color-blue-600)',
    remove: 'var(--tc-color-red-600)',
    move: 'var(--tc-color-yellow-700)',
    error: 'var(--tc-color-red-600)',
};

const DryRunModal = ({
    applianceNome,
    show,
    loading,
    acoes,
    erro,
    onClose,
    onConfirm,
    confirmLabel = 'Sincronizar agora',
    confirmLoading = false,
}: Props) => {
    const confirmDisabled = loading || !!erro || confirmLoading;
    const confirmVisual = confirmLoading ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Icon path={mdiLoading} size={0.8} spin />
            Sincronizando...
        </span>
    ) : (
        confirmLabel
    );
    return (
        <Modal
            show={show}
            title={`Simulação (dry run)${applianceNome ? ` — ${applianceNome}` : ''}`}
            onClose={onClose}
            onConfirm={onConfirm}
            onCancel={onClose}
            hideControls={!onConfirm}
            confirmProps={{ label: confirmVisual, disabled: confirmDisabled }}
            cancelProps={{ label: 'Fechar' }}
            size="lg"
        >
            {loading && (
                <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                    <Icon path={mdiLoading} size={1.5} spin />
                </HBox>
            )}

            {!loading && erro && (
                <p
                    style={{
                        color: 'var(--tc-color-red-700)',
                        background: 'var(--tc-color-red-50)',
                        borderRadius: '6px',
                        padding: '12px',
                        fontSize: '13px',
                    }}
                >
                    <strong>Erro ao simular:</strong> {erro}
                </p>
            )}

            {!loading && !erro && (
                <VBox style={{ gap: '12px' }}>
                    <p style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)', margin: 0 }}>
                        As ações abaixo <strong>seriam</strong> executadas em uma sincronização real.
                        Nenhuma alteração foi feita.
                    </p>

                    {acoes.length === 0 ? (
                        <p
                            style={{
                                color: 'var(--tc-color-green-700)',
                                background: 'var(--tc-color-green-50)',
                                borderRadius: '6px',
                                padding: '12px',
                                fontSize: '13px',
                            }}
                        >
                            ✓ Nenhuma ação necessária. A appliance já está sincronizada.
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: 'var(--tc-color-gray-100)', textAlign: 'left' }}>
                                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Área</th>
                                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Tipo</th>
                                        <th style={{ padding: '6px 10px', fontWeight: 600 }}>Dados</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {acoes.map((acao, i) => {
                                        const isError = acao.status === 'error';
                                        return (
                                            <tr
                                                key={i}
                                                style={{
                                                    borderBottom: '1px solid var(--tc-color-gray-200)',
                                                    background: isError ? 'var(--tc-color-red-50)' : undefined,
                                                }}
                                            >
                                                <td
                                                    style={{
                                                        padding: '6px 10px',
                                                        fontFamily: 'monospace',
                                                        color: isError ? 'var(--tc-color-red-700)' : 'var(--tc-color-gray-700)',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {acao.area}
                                                </td>
                                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                            color: TIPO_COLOR[acao.tipo] ?? 'inherit',
                                                        }}
                                                    >
                                                        {TIPO_LABEL[acao.tipo] ?? acao.tipo}
                                                    </span>
                                                </td>
                                                <td
                                                    style={{
                                                        padding: '6px 10px',
                                                        maxWidth: '380px',
                                                        wordBreak: 'break-all',
                                                    }}
                                                >
                                                    <DryRunDadosCell dados={acao.dados} anterior={acao.anterior} erro={acao.erro} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </VBox>
            )}
        </Modal>
    );
};

export default DryRunModal;
